import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import type { JuiImageEntry, JuiImageRejection } from '@jump-tech-frontend/core-components/components';
import {
  AnalysisScope,
  ClassificationResult,
  classificationTypeOf,
  ClassificationType
} from '@jump-tech-frontend/domain';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import {
  CameraI18nKeys,
  detectedBoxesOf,
  ImageAnalysisRecordService,
  ImageClassificationService,
  isStoredImageUrl,
  parseStoredAsset,
  PhotoCaptureTrigger,
  StoredImageLoader
} from '@jump-tech-frontend/image-analysis';
import { firstValueFrom } from 'rxjs';
import { TRANSLATIONS_PROVIDER } from '../../translations.token';
import { CAMERA_KEYS, REJECTION_KEYS } from '../capture-wording';
import { analysingVerdict, buildVerdictVm, equipmentToggleLabel, unavailableVerdict } from './image-capture-verdict';
import {
  AiValidationI18n,
  ImageAnswer,
  ImageCaptureQuestionParams,
  ImageCaptureVm,
  ImageVerdictVm
} from './image-capture.model';

interface CapturedImage {
  id: string;
  answer: string;
  displaySrc: string;
  displayState: JuiImageEntry['state'];
  analysisId?: string;
  verdict: ImageVerdictVm | null;
  result: ClassificationResult | null;
  analysisPending?: boolean;
  showBoxes?: boolean;
}

interface AnalysisRequest {
  analysisScope: AnalysisScope;
  classificationType?: ClassificationType;
}

export function analysisRequestFor(params: ImageCaptureQuestionParams): AnalysisRequest | null {
  const classificationType = classificationTypeOf(params.classification);
  if (classificationType) {
    return { analysisScope: 'classification', classificationType };
  }
  return params.qualityCheck ? { analysisScope: 'quality' } : null;
}

function answerSource(answer: ImageAnswer): string {
  return typeof answer === 'string' ? answer : answer.imageData;
}

function answerAnalysisId(answer: ImageAnswer): string | undefined {
  return typeof answer === 'string' ? undefined : answer.analysisId;
}

@Injectable()
export class ImageCapturePresenter {
  private readonly classification = inject(ImageClassificationService);
  private readonly analysisRecords = inject(ImageAnalysisRecordService);
  private readonly storedImages = inject(StoredImageLoader);
  private readonly photoCapture = inject(PhotoCaptureTrigger);
  private readonly translations = inject(TRANSLATIONS_PROVIDER);

  private readonly params = signal<ImageCaptureQuestionParams>({ key: '', label: '' });
  private readonly images = signal<CapturedImage[]>([]);
  private readonly rejection = signal<string | null>(null);
  private nextStoredId = 0;
  private emitValue: (value: ImageAnswer[]) => void = () => undefined;

  public readonly vm = computed<ImageCaptureVm>(() => {
    const readonly = this.params().readonly ?? false;
    const i18n = this.aiValidationI18n();
    return {
      key: this.params().key,
      sort: this.params().sort ?? 'desc',
      readonly,
      images: this.images().map(image => ({ id: image.id, src: image.displaySrc, state: image.displayState })),
      items: this.images().map(image => ({
        id: image.id,
        verdictSlot: `image-${image.id}`,
        actionsSlot: `image-${image.id}-actions`,
        overlaySlot: `image-${image.id}-overlay`,
        verdict: image.verdict,
        result: image.result,
        analysisPending: image.analysisPending ?? false,
        boxes: detectedBoxesOf(image.result),
        showBoxes: image.showBoxes ?? !readonly
      })),
      toggleLabel: equipmentToggleLabel(this.params().classification, i18n),
      detailI18ns: { aiValidation: i18n } as I18nKeys,
      examples: this.images().length || readonly ? [] : (this.params().examples ?? []),
      imageQa: this.params().qaHook ?? null,
      rejection: this.rejection()
    };
  });

  public readonly analysing = computed(() => this.images().some(image => image.verdict?.analysing === true));

  constructor() {
    inject(DestroyRef).onDestroy(() => this.images().forEach(image => this.storedImages.release(image.displaySrc)));
  }

  public onValueChange(emit: (value: ImageAnswer[]) => void): void {
    this.emitValue = emit;
  }

  public load(params: ImageCaptureQuestionParams): void {
    this.params.set(params);
    const answers = params.value ?? [];
    if (this.matchesCurrentAnswers(answers)) {
      return;
    }

    const current = this.images();
    const counterparts = answers.length === current.length ? current : [];
    const next = answers.map((answer, index) => this.reuseOrAdd(answer, current, counterparts[index]));
    this.releaseRemoved(current, next);
    this.images.set(next);
  }

  public imagesChanged(entries: JuiImageEntry[]): void {
    this.rejection.set(null);
    const current = this.images();
    const request = analysisRequestFor(this.params());
    const added: CapturedImage[] = [];
    const next = entries.map(entry => {
      const existing = current.find(image => image.id === entry.id);
      if (existing) {
        return existing;
      }
      const image: CapturedImage = {
        id: entry.id,
        answer: entry.src,
        displaySrc: entry.src,
        displayState: 'ready',
        verdict: request ? analysingVerdict(this.aiValidationI18n()) : null,
        result: null
      };
      added.push(image);
      return image;
    });

    this.releaseRemoved(current, next);
    this.images.set(next);
    this.emitValue(this.formValue());
    if (request) {
      added.forEach(image => void this.analyse(image.id, image.answer, request));
    }
  }

  public toggleBoxes(id: string): void {
    const readonly = this.params().readonly ?? false;
    this.images.update(images =>
      images.map(image => (image.id === id ? { ...image, showBoxes: !(image.showBoxes ?? !readonly) } : image))
    );
  }

  public rejected(rejection: JuiImageRejection): void {
    const key = REJECTION_KEYS[rejection.reason];
    this.rejection.set(key ? this.translations.getTranslation(key) : null);
  }

  public offersCamera(): boolean {
    return this.photoCapture.isAvailable();
  }

  public captureWithCamera(openPicker: () => void): Promise<string | null> {
    return this.photoCapture.capture(openPicker, this.cameraI18n());
  }

  private matchesCurrentAnswers(answers: ImageAnswer[]): boolean {
    const current = this.images();
    return (
      answers.length === current.length &&
      answers.every((answer, index) => answerSource(answer) === current[index].answer)
    );
  }

  private reuseOrAdd(answer: ImageAnswer, current: CapturedImage[], counterpart?: CapturedImage): CapturedImage {
    const source = answerSource(answer);
    const existing = current.find(image => image.answer === source);
    if (existing) {
      return { ...existing, analysisId: existing.analysisId ?? answerAnalysisId(answer) };
    }

    const uploaded = this.uploadedAs(source, counterpart);
    if (uploaded) {
      return uploaded;
    }

    this.nextStoredId += 1;
    const result = this.analysisRecords.rememberedAnalysis(answerAnalysisId(answer));
    const image: CapturedImage = {
      id: `stored-${this.nextStoredId}`,
      answer: source,
      displaySrc: isStoredImageUrl(source) ? '' : source,
      displayState: isStoredImageUrl(source) ? 'loading' : 'ready',
      analysisId: answerAnalysisId(answer),
      verdict: result ? buildVerdictVm(result, this.aiValidationI18n()) : null,
      result,
      analysisPending: isStoredImageUrl(source) && this.awaitsStoredAnalysis(result)
    };
    if (isStoredImageUrl(source)) {
      void this.loadStored(image);
    }
    return image;
  }

  private uploadedAs(source: string, shown?: CapturedImage): CapturedImage | null {
    if (!shown || isStoredImageUrl(shown.answer) || !isStoredImageUrl(source)) {
      return null;
    }

    const analysisPending = this.awaitsStoredAnalysis(shown.result);
    if (analysisPending) {
      void this.loadStoredVerdict(shown.id, source);
    }
    return { ...shown, answer: source, analysisPending };
  }

  private awaitsStoredAnalysis(result: ClassificationResult | null): boolean {
    return !result && analysisRequestFor(this.params()) !== null;
  }

  private async loadStored(image: CapturedImage): Promise<void> {
    const verdict = image.analysisPending ? this.loadStoredVerdict(image.id, image.answer) : null;
    await Promise.all([this.loadStoredDisplay(image.id, image.answer), verdict]);
  }

  private async loadStoredDisplay(id: string, source: string): Promise<void> {
    try {
      const displaySrc = await this.storedImages.thumbnailUrlFor(source);
      this.update(id, { displaySrc, displayState: 'ready' });
    } catch {
      this.update(id, { displayState: 'error' });
    }
  }

  private async loadStoredVerdict(id: string, source: string): Promise<void> {
    const result = await this.storedAnalysisFor(source);
    this.update(id, {
      analysisPending: false,
      ...(result && { verdict: buildVerdictVm(result, this.aiValidationI18n()), result })
    });
  }

  private async storedAnalysisFor(source: string): Promise<ClassificationResult | null> {
    const asset = parseStoredAsset(source);
    if (!asset) {
      return null;
    }
    try {
      return await firstValueFrom(this.analysisRecords.getAnalysisForAsset(asset));
    } catch {
      return null;
    }
  }

  private async analyse(id: string, image: string, request: AnalysisRequest): Promise<void> {
    const i18n = this.aiValidationI18n();
    let result: ClassificationResult | null = null;
    try {
      result = await firstValueFrom(
        this.classification.classifyImage({
          base64Image: image,
          deviceContext: this.params().deviceContext,
          projectId: this.params().projectId,
          ...request
        })
      );
    } catch {
      result = null;
    }

    if (result) {
      this.analysisRecords.remember(result);
    }
    const verdict = result ? buildVerdictVm(result, i18n) : unavailableVerdict(i18n);
    if (this.update(id, { verdict, result, analysisId: result?.analysisId })) {
      this.emitValue(this.formValue());
    }
  }

  private update(id: string, changes: Partial<CapturedImage>): boolean {
    const exists = this.images().some(image => image.id === id);
    if (exists) {
      this.images.update(images => images.map(image => (image.id === id ? { ...image, ...changes } : image)));
    }
    return exists;
  }

  private releaseRemoved(current: CapturedImage[], next: CapturedImage[]): void {
    current
      .filter(image => !next.some(kept => kept.id === image.id))
      .forEach(image => this.storedImages.release(image.displaySrc));
  }

  private formValue(): ImageAnswer[] {
    return this.images().map(image =>
      image.analysisId ? { imageData: image.answer, analysisId: image.analysisId } : image.answer
    );
  }

  private aiValidationI18n(): AiValidationI18n {
    return this.translations.getTranslation('aiValidation') as unknown as AiValidationI18n;
  }

  private cameraI18n(): CameraI18nKeys {
    return Object.fromEntries(CAMERA_KEYS.map(key => [key, this.translations.getTranslation(key)])) as CameraI18nKeys;
  }
}
